import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRealtimeWorkflows } from '@/hooks/useRealtimeWorkflows';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2,
  Grid3x3,
  Image,
  Video,
  Trash2,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  FileText,
  EyeOff,
  Eye,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

const OUTPUTS_PER_PAGE = 12;

export default function OutputsGridPage() {
  const navigate = useNavigate();
  const {
    executions,
    loading,
    error,
    cancelExecution,
    refreshExecutionOutputs,
    hasMoreExecutions,
    nextExecutionsPage,
    setAccumulateExecutions,
    setExecutionStatusFilter,
    loadingMoreExecutions,
    toggleHideOutput,
  } = useRealtimeWorkflows();

  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfBusyId, setPdfBusyId] = useState<string | null>(null);
  const [hidingOutputs, setHidingOutputs] = useState<Set<string>>(new Set());
  const [hiddenFilter, setHiddenFilter] = useState<'active' | 'hidden' | 'all'>('active');

  // Grow the list as the user pages through — "Load more" appends older
  // executions rather than swapping the window.
  useEffect(() => {
    setAccumulateExecutions(true);
  }, [setAccumulateExecutions]);

  // Only completed runs produce outputs — filter server-side so error/queued
  // runs don't consume the page budget and bury real outputs further back.
  useEffect(() => {
    setExecutionStatusFilter('completed');
    return () => setExecutionStatusFilter(undefined);
  }, [setExecutionStatusFilter]);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [hiddenFilter]);

  const handleReviewPdf = async (executionId: string) => {
    if (pdfBusyId) return;
    const execRecord = executions.find((e) => e.id === executionId);
    if (!execRecord) return;
    setPdfBusyId(executionId);
    try {
      const { downloadReviewPdf } = await import('@/lib/reviewDoc');
      await downloadReviewPdf(execRecord);
    } catch {
      alert('Failed to generate the review document');
    } finally {
      setPdfBusyId(null);
    }
  };

  const handleToggleHideOutput = async (executionId: string, filename: string, hidden: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    const outputKey = `${executionId}-${filename}`;
    setHidingOutputs((prev) => new Set(prev).add(outputKey));
    try {
      await toggleHideOutput(executionId, filename, hidden);
    } catch (err) {
      console.error('Failed to toggle hide output:', err);
      alert('Failed to toggle hide output');
    } finally {
      setHidingOutputs((prev) => {
        const next = new Set(prev);
        next.delete(outputKey);
        return next;
      });
    }
  };
  // Track which executions we've already asked the backend to re-sign, so we
  // don't re-request on every listener tick.
  const refreshedRef = useRef<Set<string>>(new Set());

  const handleSoftDelete = async (promptId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Remove this job from the queue?')) {
      setDeletingIds((prev) => new Set(prev).add(promptId));
      try {
        await cancelExecution(promptId);
      } catch {
        alert('Failed to cancel job');
      } finally {
        setDeletingIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(promptId);
          return newSet;
        });
      }
    }
  };

  // Filter executions with outputs and queued jobs
  const completedWithOutputs = executions.filter((exec) => exec.status === 'completed' && exec.outputs.length > 0);
  const allQueuedJobs = executions.filter((exec) => exec.status === 'queued');
  const queuedJobs = allQueuedJobs.slice(0, 5);

  // Get all outputs from completed executions (filter based on hidden status)
  const allOutputs = completedWithOutputs.flatMap((exec) =>
    exec.outputs
      .filter((output) => {
        const isHidden = output.hidden === true;
        if (hiddenFilter === 'hidden') return isHidden;
        if (hiddenFilter === 'active') return !isHidden;
        return true; // 'all' shows everything
      })
      .map((output) => ({
        ...output,
        executionId: exec.id,
        workflowName: exec.workflowName,
        createdAt: exec.createdAt,
      })),
  );

  // Calculate pagination. While the server still has older executions to fetch,
  // only expose FULL pages — the trailing partial page (the tail of the loaded
  // outputs) is hidden so a page is never shown half-empty and then reshuffled
  // when the next batch arrives. The final partial page is revealed only once
  // the server is exhausted (no more executions to load).
  const totalOutputs = allOutputs.length;
  const fullPages = Math.floor(totalOutputs / OUTPUTS_PER_PAGE);
  const totalPages = hasMoreExecutions ? Math.max(1, fullPages) : Math.max(1, Math.ceil(totalOutputs / OUTPUTS_PER_PAGE));
  const startIndex = (currentPage - 1) * OUTPUTS_PER_PAGE;
  const endIndex = startIndex + OUTPUTS_PER_PAGE;
  const paginatedOutputs = allOutputs.slice(startIndex, endIndex);

  // Next walks the loaded full pages, then transparently fetches the next server
  // batch of older executions when they run out — one button, no separate "load
  // more". Because a partial tail page stays hidden until data fills it, we can't
  // advance the page number immediately on fetch (the next full page doesn't
  // exist yet). Instead we record the intent and advance once it materializes
  // (see the effect below).
  const pendingAdvanceRef = useRef(false);
  const canGoNext = currentPage < totalPages || hasMoreExecutions;
  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage((p) => p + 1);
    } else if (hasMoreExecutions) {
      pendingAdvanceRef.current = true;
      nextExecutionsPage();
    }
  };

  // Once a fetch triggered by Next lands, advance to the newly opened page. If
  // the batch added outputs but not enough to open a full page (and more remain
  // server-side), keep fetching until a page opens or the server is exhausted —
  // so Next always lands the user on a full page, never a half-empty one.
  useEffect(() => {
    if (!pendingAdvanceRef.current || loadingMoreExecutions) return;
    if (currentPage < totalPages) {
      pendingAdvanceRef.current = false;
      setCurrentPage((p) => p + 1);
    } else if (hasMoreExecutions) {
      nextExecutionsPage();
    } else {
      // Server exhausted and no further full page — clear the intent (the final
      // partial page is now the current page).
      pendingAdvanceRef.current = false;
    }
  }, [loadingMoreExecutions, totalPages, currentPage, hasMoreExecutions, nextExecutionsPage]);

  // Ask the backend to re-sign output URLs for the executions visible on this
  // page. The re-signed URLs persist to Firestore and flow back via the
  // realtime listener. Each execution is refreshed at most once per mount.
  useEffect(() => {
    const visibleIds = new Set(paginatedOutputs.map((o) => o.executionId));
    for (const id of visibleIds) {
      if (refreshedRef.current.has(id)) continue;
      refreshedRef.current.add(id);
      refreshExecutionOutputs(id);
    }
  }, [paginatedOutputs, refreshExecutionOutputs]);

  const isImage = (filename: string) => {
    return (
      filename.endsWith('.png') ||
      filename.endsWith('.jpg') ||
      filename.endsWith('.jpeg') ||
      filename.endsWith('.webp') ||
      filename.endsWith('.gif')
    );
  };

  const isVideo = (filename: string) => {
    return filename.endsWith('.mp4') || filename.endsWith('.webm') || filename.endsWith('.mov');
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="h-full bg-gradient-to-br from-background via-background to-accent/30 dark:to-accent/10 p-8 overflow-auto">
      <div className="max-w-7xl mx-auto">
        <header className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <Grid3x3 className="h-6 w-6" />
                My Outputs
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {allOutputs.length === 0
                  ? 'No outputs yet'
                  : `${allOutputs.length} ${allOutputs.length === 1 ? 'output' : 'outputs'} from completed workflows`}
              </p>
            </div>
            {(totalPages > 1 || hasMoreExecutions) && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground px-2">Page {currentPage}</span>
                <Button variant="outline" size="sm" onClick={handleNext} disabled={!canGoNext || loadingMoreExecutions}>
                  {loadingMoreExecutions ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4" />}
                </Button>
              </div>
            )}
          </div>
        </header>

        {error && (
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg mb-6">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Filter Tabs */}
        <Tabs value={hiddenFilter} onValueChange={(v) => setHiddenFilter(v as 'active' | 'hidden' | 'all')} className="mb-6">
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="active" className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Active
            </TabsTrigger>
            <TabsTrigger value="hidden" className="flex items-center gap-2">
              <EyeOff className="h-4 w-4" />
              Hidden
            </TabsTrigger>
            <TabsTrigger value="all" className="flex items-center gap-2">
              <Grid3x3 className="h-4 w-4" />
              All
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Queued Jobs Section */}
        {allQueuedJobs.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin text-violet-500" />
                Jobs in Queue ({allQueuedJobs.length})
              </h2>
              {allQueuedJobs.length > 5 && (
                <Button variant="outline" size="sm" onClick={() => navigate('/history')} className="text-xs">
                  View All
                </Button>
              )}
            </div>
            <div className="space-y-2">
              {queuedJobs.map((job) => (
                <Card key={job.id} className="border-border/60 bg-accent/20">
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <Loader2 className="h-5 w-5 animate-spin text-violet-500 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm truncate">{job.workflowName}</div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-muted-foreground">
                              {new Date(job.createdAt).toLocaleString(undefined, {
                                month: 'short',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                              })}
                            </span>
                            <Badge variant="secondary" className="text-[10px]">
                              queued
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0"
                        onClick={(e) => handleSoftDelete(job.id, e)}
                        disabled={deletingIds.has(job.id)}
                      >
                        {deletingIds.has(job.id) ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Trash2 className="h-4 w-4 mr-1" />
                            Remove
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Outputs Grid */}
        {allOutputs.length === 0 ? (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-accent mb-4">
              <Grid3x3 className="h-8 w-8 text-muted-foreground/40" />
            </div>
            <h3 className="text-lg font-medium text-muted-foreground">No outputs yet</h3>
            <p className="text-sm text-muted-foreground/60 mt-1">Complete a workflow to see outputs here</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {paginatedOutputs.map((output, index) => (
                <div
                  key={`${output.executionId}-${index}`}
                  className={
                    'group relative aspect-square rounded-xl overflow-hidden ' +
                    'border border-border/60 hover:border-violet-500/50 ' +
                    'transition-all hover:shadow-lg'
                  }
                >
                  <button
                    type="button"
                    onClick={() => navigate(`/history/${output.executionId}`)}
                    className="absolute inset-0 w-full h-full cursor-pointer"
                  >
                    {isImage(output.filename) && (
                      <img src={output.url} alt={output.filename} className="w-full h-full object-cover" />
                    )}
                    {isVideo(output.filename) && (
                      <video src={output.url} className="w-full h-full object-cover" muted playsInline />
                    )}
                    {!isImage(output.filename) && !isVideo(output.filename) && (
                      <div className={'w-full h-full bg-accent/30 flex items-center justify-center'}>
                        <div className="text-center">
                          <div className="text-4xl mb-2">📄</div>
                          <p className="text-xs text-muted-foreground px-2 break-words">{output.filename}</p>
                        </div>
                      </div>
                    )}

                    {/* Overlay */}
                    <div
                      className={
                        'absolute inset-0 bg-black/0 group-hover:bg-black/60 ' +
                        'transition-all flex flex-col justify-end p-3 ' +
                        'opacity-0 group-hover:opacity-100'
                      }
                    >
                      <div className="flex items-center gap-1 mb-1">
                        {isImage(output.filename) && <Image className="h-3 w-3 text-white" />}
                        {isVideo(output.filename) && <Video className="h-3 w-3 text-white" />}
                        <span className="text-xs text-white font-medium truncate">{output.workflowName}</span>
                      </div>
                      <p className="text-[10px] text-white/80 truncate">{output.filename}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <CheckCircle className="h-3 w-3 text-green-400" />
                        <span className="text-[10px] text-white/60">
                          {new Date(output.createdAt).toLocaleDateString(undefined, {
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                      </div>
                    </div>
                  </button>

                  {/* Action buttons — siblings of the navigate button (never nested) */}
                  <div className="absolute top-2 right-2 z-10 flex gap-1">
                    <button
                      type="button"
                      title={output.hidden ? 'Show in My Outputs' : 'Hide from My Outputs'}
                      onClick={(e) => handleToggleHideOutput(output.executionId, output.filename, !output.hidden, e)}
                      disabled={hidingOutputs.has(`${output.executionId}-${output.filename}`)}
                      className={
                        'p-2 rounded-lg ' +
                        'bg-black/50 text-white hover:bg-black/70 backdrop-blur-sm transition-all ' +
                        'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto ' +
                        'focus-visible:opacity-100 focus-visible:pointer-events-auto disabled:opacity-50'
                      }
                    >
                      {hidingOutputs.has(`${output.executionId}-${output.filename}`) ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : output.hidden ? (
                        <Eye className="h-4 w-4" />
                      ) : (
                        <EyeOff className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      type="button"
                      title="Download legal review PDF"
                      onClick={() => handleReviewPdf(output.executionId)}
                      disabled={pdfBusyId !== null}
                      className={
                        'p-2 rounded-lg ' +
                        'bg-black/50 text-white hover:bg-black/70 backdrop-blur-sm transition-all ' +
                        'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto ' +
                        'focus-visible:opacity-100 focus-visible:pointer-events-auto disabled:opacity-50'
                      }
                    >
                      {pdfBusyId === output.executionId ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <FileText className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom Pagination — Next transparently fetches older executions
                when the loaded outputs run out (no separate "load more"). */}
            {(totalPages > 1 || hasMoreExecutions) && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground px-4">Page {currentPage}</span>
                <Button variant="outline" size="sm" onClick={handleNext} disabled={!canGoNext || loadingMoreExecutions}>
                  {loadingMoreExecutions ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                      Loading…
                    </>
                  ) : (
                    <>
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </>
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
